import { z } from 'zod'
import { compareAppVersions, DEFAULT_STORE_URLS } from '../app-version'
import { accessGrantsSettingsSchema, DEFAULT_ACCESS_GRANTS_SETTINGS } from './access-grants'
import { DEFAULT_PLAN_QUOTAS, planQuotasSchema } from './plans'
import { MARKETS, type Language, type Market } from './simulation'
import { CERT_TARGETS, CERT_TARGETS_AVAILABLE } from '../cert/targets'
import { aiConsentSettingsSchema, DEFAULT_AI_CONSENT_SETTINGS } from './ai-consent'
import {
  PRODUCT_REVIEW_KEEP,
  PRODUCT_REVIEW_THRESHOLDS,
  productReviewThresholdsSchema,
} from '../product-review'

/** '' = 안내 없음. 그 외에는 '1.2.3' 형식 (app-version.ts parseAppVersion 과 같은 규칙) */
const appVersionField = z
  .string()
  .regex(/^(\d{1,4}\.\d{1,4}\.\d{1,4})?$/, '버전은 1.2.3 형식으로 쓰거나 비워 둡니다')
  .default('')

/** 플랫폼 하나의 새 버전 안내 */
const appUpdatePlatformSchema = (fallbackStoreUrl: string) =>
  z
    .object({
      /** 이 버전 미만이면 닫을 수 없는 업데이트 화면. 그 버전이 해당 스토어에 실제로 공개된 뒤에만 올린다 */
      minVersion: appVersionField,
      /** 이 버전 미만이면 닫을 수 있는 안내 (버전마다 한 번) */
      latestVersion: appVersionField,
      /** [업데이트] 버튼이 여는 스토어 주소. '' 이면 앱이 DEFAULT_STORE_URLS 를 쓴다 */
      storeUrl: z
        .union([z.literal(''), z.url({ protocol: /^https$/ }).max(500)])
        .default(fallbackStoreUrl),
    })
    .refine(
      (p) =>
        !p.minVersion ||
        !p.latestVersion ||
        compareAppVersions(p.minVersion, p.latestVersion) !== 1,
      { message: '최소 버전이 권장 버전보다 높을 수 없습니다', path: ['minVersion'] },
    )

/**
 * 새 버전 안내 (2026-10-08, 1.0.1 부터 앱이 읽음). 1.0.0 앱은 이 필드를 모른다.
 * iOS·Android 심사·출시 시점이 달라 플랫폼별로 둔다. 공통 하나로 두면 한쪽만 출시됐을 때 다른 쪽 사용자가
 * 받을 수 없는 업데이트 화면에 갇힌다.
 */
export const appUpdateSettingsSchema = z.object({
  ios: appUpdatePlatformSchema(DEFAULT_STORE_URLS.ios),
  android: appUpdatePlatformSchema(DEFAULT_STORE_URLS.android),
})
export type AppUpdateSettings = z.infer<typeof appUpdateSettingsSchema>

/** 기본값 = 안내 없음. 서버를 먼저 배포해도 아무 앱에도 변화가 없다 */
export const DEFAULT_APP_UPDATE_SETTINGS: AppUpdateSettings = {
  ios: { minVersion: '', latestVersion: '', storeUrl: DEFAULT_STORE_URLS.ios },
  android: { minVersion: '', latestVersion: '', storeUrl: DEFAULT_STORE_URLS.android },
}

/** 플랫폼 하나의 간편 로그인 버튼 노출. 둘 다 기본 꺼짐 */
const socialSignInPlatformSchema = z.object({
  apple: z.boolean().default(false),
  google: z.boolean().default(false),
})

/**
 * Apple·Google 간편 로그인 버튼 노출 (2026-10-08, 1.0.1 부터 앱이 읽음). 1.0.0 앱은 이 필드를 모르고 버튼을 항상 숨긴다.
 * 로그인 흐름(Clerk SSO)은 앱에 이미 있고, 이 값은 버튼을 보일지만 정한다. Clerk 운영 인스턴스에 Apple·Google 자격증명이
 * 없으면 눌러도 실패하므로 그 설정을 먼저 한다. iOS·Android 심사·출시 시점이 달라 플랫폼별로 둔다 (iOS 심사 중 Android 만 먼저).
 * iOS 에서 Google 만 켜는 값은 거부한다 — App Store 가이드라인 4.8 (다른 소셜 로그인을 보이면 Apple 로그인도 같이)
 */
export const socialSignInSettingsSchema = z
  .object({
    ios: socialSignInPlatformSchema.default({ apple: false, google: false }),
    android: socialSignInPlatformSchema.default({ apple: false, google: false }),
  })
  .refine((s) => !s.ios.google || s.ios.apple, {
    message:
      'iOS 에서 Google 로그인을 켜려면 Apple 로그인도 켜야 합니다 (App Store 가이드라인 4.8)',
    path: ['ios', 'google'],
  })
export type SocialSignInSettings = z.infer<typeof socialSignInSettingsSchema>

/** 기본값 = 지금처럼 버튼 숨김 (이메일 코드 로그인만). 서버를 먼저 배포해도 아무 앱에도 변화가 없다 */
export const DEFAULT_SOCIAL_SIGN_IN_SETTINGS: SocialSignInSettings = {
  ios: { apple: false, google: false },
  android: { apple: false, google: false },
}

/**
 * 이 기기(os = Platform.OS)의 로그인 화면에 보일 간편 로그인 버튼. 앱과 API 테스트가 같은 함수를 쓴다.
 * - 옛 서버라 칸이 없거나(undefined), 오프라인 기본값이거나, iOS·Android 가 아니면(웹 등) 둘 다 숨긴다 (지금 동작)
 * - iOS 에서는 Apple 이 꺼져 있으면 Google 도 숨긴다 (가이드라인 4.8). 서버 검증을 우회한 값(KV 직접 수정 등)이 와도
 *   앱이 iOS 에서 Google 만 보이는 상태가 나오지 않게 하는 두 번째 방어선이다
 */
export function socialSignInButtons(
  settings: SocialSignInSettings | null | undefined,
  os: string,
): { apple: boolean; google: boolean } {
  if (os !== 'ios' && os !== 'android') return { apple: false, google: false }
  const platform = settings?.[os]
  const apple = platform?.apple === true
  const google = platform?.google === true && (os !== 'ios' || apple)
  return { apple, google }
}

/**
 * 완성 사례 갤러리 (2단계, 2026-10-22). 서버 코드 쪽 스위치(GET /config 의 features.sampleCases)와 함께 두 겹이다.
 * 앱은 둘 다 참일 때만 진입점을 그린다 (sampleCasesVisible). 처음 배포 때는 꺼 둔다
 */
export const sampleCasesSettingsSchema = z.object({
  /** 갤러리 보이기. 꺼져 있으면 GET /cases 가 404, 글 응답의 caseId 도 null */
  enabled: z.boolean().default(false),
  /** 모든 사례의 '이 사례로 시작하기' 허용. AI 비용이 갑자기 늘 때 끄는 비상 스위치 */
  startEnabled: z.boolean().default(true),
  /**
   * 앱 언어와 다른 언어의 사례로도 시작하기 허용 (2026-10-22, 반박 검토 M2). 사례 글은 사례 언어인데 생성 검수는 결과가
   * 앱 언어 한 가지인지 본다. 이 조합이 개발 서버에서 확인되기 전까지 꺼 둔다. 꺼져 있으면 앱이 다른 언어 사례의 시작 버튼을
   * 그리지 않는다 (보기는 된다)
   */
  crossLanguageStart: z.boolean().default(false),
})
export type SampleCasesSettings = z.infer<typeof sampleCasesSettingsSchema>

/** 기본값 = 갤러리 숨김. 서버를 먼저 배포해도 아무 앱에도 변화가 없다 */
export const DEFAULT_SAMPLE_CASES_SETTINGS: SampleCasesSettings = {
  enabled: false,
  startEnabled: true,
  crossLanguageStart: false,
}

/**
 * AI 맛 예측 카드 (2026-10-25 대표 지시: 바로 보이게). 서버 코드 쪽 스위치(GET /config 의 features.tastePrediction)와 두 겹이다.
 * 기본 켬 — 예전 KV 값에 칸이 없어도 켜진다. 끄면 서버가 새 추정을 만들지 않고(AI 비용이 갑자기 늘 때 끄는 스위치) 앱은 카드를 그리지 않는다
 */
export const tastePredictionSettingsSchema = z.object({ enabled: z.boolean().default(true) })
export type TastePredictionSettings = z.infer<typeof tastePredictionSettingsSchema>
export const DEFAULT_TASTE_PREDICTION_SETTINGS: TastePredictionSettings = { enabled: true }

/** 공지 띠 문구 한 언어의 최대 길이. 띠는 두세 줄을 넘기지 않는다 */
export const APP_NOTICE_TEXT_MAX = 200
const appNoticeTextField = z.string().trim().max(APP_NOTICE_TEXT_MAX).default('')

/**
 * 앱 안 공지 띠 (C20, 2026-10-25). 홈 맨 위에 띄우는 장애·점검·정책 변경 안내. 가입자 푸시만으로는 손님과 푸시를 거부한
 * 회원에게 닿지 않아서 둔다. 기본 꺼짐. 서버 코드 쪽 스위치는 따로 두지 않는다: 이 칸을 모르는 옛 서버는 KV 값에서 칸을
 * 버리고, 앱은 칸이 없으면 꺼진 기본값을 쓴다. 문구는 앱 언어 것을 쓰고 비면 en (appNoticeContent)
 */
export const appNoticeSettingsSchema = z
  .object({
    enabled: z.boolean().default(false),
    /** info = 일반 안내, warning = 장애·점검 (띠 색만 다르다) */
    level: z.enum(['info', 'warning']).default('info'),
    text: z
      .object({
        ko: appNoticeTextField,
        en: appNoticeTextField,
        ja: appNoticeTextField,
        zh: appNoticeTextField,
      })
      .default({ ko: '', en: '', ja: '', zh: '' }),
    /** 띠를 누르면 여는 주소. '' 이면 누를 수 없는 띠 */
    url: z.union([z.literal(''), z.url({ protocol: /^https$/ }).max(500)]).default(''),
  })
  .refine((n) => !n.enabled || n.text.en !== '', {
    message: '공지를 켜려면 영어 문구를 넣어야 합니다 (다른 언어 문구가 비면 영어로 보여요)',
    path: ['text', 'en'],
  })
export type AppNoticeSettings = z.infer<typeof appNoticeSettingsSchema>

/** 기본값 = 띠 없음. 서버를 먼저 배포해도 아무 앱에도 변화가 없다 */
export const DEFAULT_APP_NOTICE_SETTINGS: AppNoticeSettings = {
  enabled: false,
  level: 'info',
  text: { ko: '', en: '', ja: '', zh: '' },
  url: '',
}

/**
 * 이 언어 사용자에게 보일 공지 띠. 꺼져 있거나, 옛 서버라 칸이 없거나, 보일 문구가 없으면 null.
 * 앱 언어 문구가 비면 en 을 쓴다. 앱과 API 테스트가 같은 함수를 쓴다
 */
export function appNoticeContent(
  settings: { appNotice?: AppNoticeSettings } | null | undefined,
  lang: Language,
): { level: AppNoticeSettings['level']; text: string; url: string | null } | null {
  const notice = settings?.appNotice
  if (notice?.enabled !== true) return null
  const text = notice.text[lang]?.trim() || notice.text.en?.trim()
  if (!text) return null
  return { level: notice.level === 'warning' ? 'warning' : 'info', text, url: notice.url || null }
}

/**
 * 견적 접수 (C36, 2026-10-25). 공장·처리 인원이 모자랄 때 관리자가 새 견적 접수를 멈추는 스위치. 기본 켬(지금처럼 받음).
 * 서버 코드 쪽 스위치(GET /config 의 features.quoteIntake)와 두 겹이다. 끄면 새 앱(X-Client-Features 에 quote-intake)은
 * 결제 전에 안내하고 결제창을 열지 않으며, 서버도 그 앱의 접수를 견적권을 쓰기 전에 거절한다(QUOTE_INTAKE_PAUSED_CODE).
 * 한계: 옛 앱은 결제한 뒤 접수를 보내므로 꺼도 서버가 받는다 — 막으면 돈만 나간다
 */
export const quoteIntakeSettingsSchema = z.object({ enabled: z.boolean().default(true) })
export type QuoteIntakeSettings = z.infer<typeof quoteIntakeSettingsSchema>
export const DEFAULT_QUOTE_INTAKE_SETTINGS: QuoteIntakeSettings = { enabled: true }
/** 견적 접수 멈춤을 결제 전에 확인하는 앱이 X-Client-Features 에 싣는 값. 이 값이 없는 요청은 멈춰도 받는다 */
export const CLIENT_FEATURE_QUOTE_INTAKE = 'quote-intake'
/** 견적 접수를 멈춰 거절한 POST /quotes 503 의 error.code */
export const QUOTE_INTAKE_PAUSED_CODE = 'QUOTE_INTAKE_PAUSED'

/**
 * 견적 가능 시장 (로드맵 P7, 2026-10-27). 해외 공장이 없는데 해외 시장(us) 제품도 견적권을 살 수 있어 결제가 곧 환불 분쟁이 됐다.
 * 그래서 견적을 받을 수 있는 시장을 정해 두고, 그 밖의 시장 제품은 결제창을 열기 전에 막고 대기 명단을 안내한다.
 * 기본 켬: 비용·환불을 막는 방어라서다(공통 지침의 '비용 방어는 기본 켬'). 예전 KV 값에 칸이 없어도 켜진다.
 * 서버 코드 쪽 스위치(GET /config 의 features.quoteMarkets)와 두 겹이다 — 새 앱은 둘 다 참일 때만 시장 밖 제품에 대기 명단을 보인다.
 * 서버는 이 설정만 보고 POST /quotes 를 견적권을 쓰기 전에 거절한다(QUOTE_MARKET_UNAVAILABLE_CODE). 시장을 모르는 옛 앱도 막아야 해서다.
 * 대기 명단 UI 는 시장 제한과 한 묶음이라 같은 enabled 를 따른다.
 * 2026-10-29 대표 결정: 견적은 한국·미국만이 아니라 여러 나라에서 받는다. 그래서 기본값은 모든 시장이고,
 * 특정 시장을 막을 때만 관리자 [설정]에서 뺀다
 */
export const DEFAULT_QUOTE_MARKETS: readonly Market[] = MARKETS
export const quoteMarketsSettingsSchema = z.object({
  /** 시장 제한 켜기. 끄면 모든 시장 제품이 예전처럼 견적을 받는다 */
  enabled: z.boolean().default(true),
  /** 견적을 받을 수 있는 시장. 막을 시장만 관리자 [설정]에서 뺀다 */
  markets: z
    .array(z.enum(MARKETS))
    .max(MARKETS.length)
    .default([...DEFAULT_QUOTE_MARKETS]),
})
export type QuoteMarketsSettings = z.infer<typeof quoteMarketsSettingsSchema>
export const DEFAULT_QUOTE_MARKETS_SETTINGS: QuoteMarketsSettings = {
  enabled: true,
  markets: [...DEFAULT_QUOTE_MARKETS],
}
/** 이 시장 제품이 견적을 받을 수 있는지. 서버 접수(POST /quotes)와 앱의 결제 전 확인이 같은 함수를 쓴다 */
export function quoteMarketOpen(settings: QuoteMarketsSettings, market: Market): boolean {
  return !settings.enabled || settings.markets.includes(market)
}
/** 견적을 받지 않는 시장의 제품이라 거절한 POST /quotes 409 의 error.code. 견적권은 쓰이지 않는다 */
export const QUOTE_MARKET_UNAVAILABLE_CODE = 'QUOTE_MARKET_UNAVAILABLE'

/**
 * 아이디어 시작 질문 (C39, 2026-10-25). 설명 화면에서 사용 목적·목표 수량·예산·판매 대상을 묻는다.
 * 서버 코드 쪽 스위치(GET /config 의 features.startQuestions)와 두 겹이다. 둘 다 참일 때만 앱이 질문을 보인다.
 * 기본 꺼짐: 선택지 값(START_GOAL_OPTIONS)을 대표가 확인하기 전이라서
 */
export const startQuestionsSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type StartQuestionsSettings = z.infer<typeof startQuestionsSettingsSchema>
export const DEFAULT_START_QUESTIONS_SETTINGS: StartQuestionsSettings = { enabled: false }

/**
 * '어떻게 알고 오셨어요?' 한 문항 (C56). 첫 완성 결과의 카드와 견적 요청서의 선택 칸.
 * 서버 코드 쪽 스위치(GET /config 의 features.heardFrom)와 두 겹이다. 둘 다 참일 때만 앱이 질문을 보인다.
 * 기본 꺼짐: 사용자에게 보이는 새 질문이라 대표가 켤 때를 정한다
 */
export const heardFromSurveySettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type HeardFromSurveySettings = z.infer<typeof heardFromSurveySettingsSchema>
export const DEFAULT_HEARD_FROM_SURVEY_SETTINGS: HeardFromSurveySettings = { enabled: false }

/**
 * 생성 취소 (C50, 2026-10-27). 생성 대기 화면에 '취소' 버튼을 보인다. 대기(queued) 중 취소는 생성 사용권을 돌려주고,
 * AI 를 이미 부른 뒤(running) 취소는 돌려주지 않는다. 서버 코드 쪽 스위치(GET /config 의 features.generationCancel)와 두 겹이다.
 * 꺼져 있으면 서버도 취소 요청을 404 로 받지 않는다. 기본 꺼짐: 새 사용자 기능이라 대표가 켜기 전까지 지금과 같게
 */
export const generationCancelSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type GenerationCancelSettings = z.infer<typeof generationCancelSettingsSchema>
export const DEFAULT_GENERATION_CANCEL_SETTINGS: GenerationCancelSettings = { enabled: false }

/**
 * 실패한 사진만 다시 만들기 (C49, 2026-10-27). 사진만 실패한 결과 화면에 버튼을 보여, 성공한 컷은 두고 실패한 컷만 다시 만든다.
 * 같은 결과의 첫 재시도는 무료, 그다음은 기존 사진 재생성 한도를 쓴다. 서버 코드 쪽 스위치(GET /config 의
 * features.photoRetryFailed)와 두 겹이고, 꺼져 있으면 서버도 요청을 받지 않는다. 기본 꺼짐: 새 사용자 기능
 */
export const failedPhotoRetrySettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type FailedPhotoRetrySettings = z.infer<typeof failedPhotoRetrySettingsSchema>
export const DEFAULT_FAILED_PHOTO_RETRY_SETTINGS: FailedPhotoRetrySettings = { enabled: false }

/**
 * 견적 취소·견적권 반환 (C48, 2026-10-27 대표 결정).
 * enabled: 앱의 '견적 요청 취소' 버튼(접수 상태·제안 0건일 때만). 새 기능이라 기본 꺼짐이고, 서버 코드 쪽 스위치
 * (GET /config 의 features.quoteCancel)와 두 겹이다. 꺼져 있으면 POST /quotes/:id/cancel 도 404 다.
 * refundNoOfferClose: 관리자가 제안 0건인 견적을 마감하면 쓴 견적권을 자동으로 돌려준다. 앱 진입점이 없는 고객 보호라
 * 기본 켬이다 — 공장 답을 못 받은 고객이 돈만 낸 채로 남지 않게. 끄면 예전처럼 돌려주지 않는다(관리자가 따로 지급)
 */
export const quoteCancelSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  refundNoOfferClose: z.boolean().default(true),
})
export type QuoteCancelSettings = z.infer<typeof quoteCancelSettingsSchema>
export const DEFAULT_QUOTE_CANCEL_SETTINGS: QuoteCancelSettings = {
  enabled: false,
  refundNoOfferClose: true,
}

/**
 * 스토어 환불 회수 (C51, 2026-10-27 대표 결정). 환불 웹훅이 오면 아직 쓰지 않은 견적권과 생성 팩의 남은 횟수를 잔액에서 뺀다.
 * 이미 쓴 몫은 자동으로 회수하지 않고 원장에 운영자 확인 대상으로만 표시한다. 앱 진입점이 없는 비용 방어라
 * features 스위치는 두지 않고 기본 켬이다 — 꺼 두면 환불받은 견적권을 계속 쓸 수 있다. 꺼도 원장 기록은 남는다
 */
export const refundRevokeSettingsSchema = z.object({ enabled: z.boolean().default(true) })
export type RefundRevokeSettings = z.infer<typeof refundRevokeSettingsSchema>
export const DEFAULT_REFUND_REVOKE_SETTINGS: RefundRevokeSettings = { enabled: true }

/**
 * 광고성 알림 수신 동의 선택 화면 (로드맵 P6, 2026-10-27). 새 앱이 회원·손님마다 한 번(동의 2년이 지나면 다시) '동의'와
 * '동의 안 함' 중 하나를 꼭 고르게 한다. 서버 코드 쪽 스위치(GET /config 의 features.marketingConsent)와 두 겹이다.
 * 법 준수 기능이라 기본 켬. 꺼도 광고성 푸시는 동의한 사람에게만 가고, 계정 탭의 철회 토글은 그대로 보인다
 */
export const marketingConsentSettingsSchema = z.object({ promptEnabled: z.boolean().default(true) })
export type MarketingConsentSettings = z.infer<typeof marketingConsentSettingsSchema>
export const DEFAULT_MARKETING_CONSENT_SETTINGS: MarketingConsentSettings = { promptEnabled: true }

/**
 * 손님 생성 남용 한도 (로드맵 P3, 2026-10-26). 기기당 한도(guestSimulationLimit)는 앱이 만든 X-Device-Id 로 세서
 * id 를 바꿔 보내면 끝없이 넘을 수 있다. 그래서 서버가 직접 보는 값(요청 IP)과 전체 합으로 한 번 더 센다. 0 = 그 한도 끔.
 * 회원 생성은 세지도 막지도 않는다. 걸리면 기기 한도와 같은 402 GUEST_LIMIT 라 앱(1.0.3~)은 가입 안내로 간다.
 * 앱에 보이는 진입점이 없는 서버 방어라 features 스위치는 두지 않고 기본 켬이다 — 생성마다 AI 비용이 들고,
 * 잘못 걸린 사람도 가입하면 바로 계속 만들 수 있다. 앱이 쓰지 않는 값이라 GET /config 로는 내보내지 않는다
 */
export const guestAbuseLimitsSettingsSchema = z.object({
  /**
   * 같은 IP(IPv6 는 같은 /64)에서 하루(UTC) 손님 생성 수. 기본 30 = 기기당 2회 × 15명.
   * 회사·카페 공유기와 통신사 NAT 뒤의 여러 사람이 한 IP 로 보이므로 한 사람 몫(2)보다 훨씬 넉넉히 둔다
   */
  perIpPerDay: z.number().int().min(0).max(10000).default(30),
  /**
   * 모든 손님 생성의 최근 1시간 합. 기본 200. 여러 IP 로 나눠 오는 남용을 멈추는 비상 차단이라 평소 손님 수보다
   * 크게 둔다. 걸리는 동안 새 손님은 가입해야 만들 수 있다
   */
  allGuestsPerHour: z.number().int().min(0).max(100000).default(200),
})
export type GuestAbuseLimitsSettings = z.infer<typeof guestAbuseLimitsSettingsSchema>
export const DEFAULT_GUEST_ABUSE_LIMITS_SETTINGS: GuestAbuseLimitsSettings = {
  perIpPerDay: 30,
  allGuestsPerHour: 200,
}

/**
 * 웹 공유 공개·비공개 모드 선택 (C54, 2026-10-30). 공유 시트에 링크마다 '공개 모드 / 비공개 모드' 선택을 보인다.
 * 서버 코드 쪽 스위치(GET /config 의 features.shareScope)와 두 겹이다. 둘 다 참일 때만 앱이 선택을 보인다.
 * 기본 꺼짐: 새 사용자 기능이라서. 꺼져 있으면 새 링크는 모두 비공개 모드(NEW_SHARE_SCOPE, 스위치와 상관없음)이고
 * 이미 만든 링크는 지금 모드 그대로 열린다
 */
export const shareScopeSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type ShareScopeSettings = z.infer<typeof shareScopeSettingsSchema>
export const DEFAULT_SHARE_SCOPE_SETTINGS: ShareScopeSettings = { enabled: false }

/**
 * 재방문 알림 (2026-10-29, 4차 카드 '재방문 알림'). 매시 크론이 상태를 보고 보내는 광고성 알림 두 가지를 따로 켠다.
 * limitReminder: 무료 생성을 다 쓰고 아직 플랜을 올리지 않은 회원에게 하루·일주일 뒤. quoteNudge: 완성한 제품에 견적 요청이 없으면 하루 뒤.
 * 서버 코드 쪽 스위치(GET /config 의 features.revisitNotifications)와 두 겹이다. 기본 꺼짐: 새 사용자 기능이라서.
 * 켜도 수신 동의한 회원에게만, 한국과 현지 모두 낮에만 '(광고)' 표기를 붙여 간다(lib/notify.ts)
 */
export const revisitNotificationsSettingsSchema = z.object({
  limitReminder: z.boolean().default(false),
  quoteNudge: z.boolean().default(false),
})
export type RevisitNotificationsSettings = z.infer<typeof revisitNotificationsSettingsSchema>
export const DEFAULT_REVISIT_NOTIFICATIONS_SETTINGS: RevisitNotificationsSettings = {
  limitReminder: false,
  quoteNudge: false,
}

/**
 * 결과 화면 원가 시나리오 (C46, 2026-10-29). 수율·충전량·포장·채널 수수료·세금을 바꿔 원가·마진을 다시 계산해 보는 접힌 칸.
 * 앱 안에서만 계산하고 저장하지 않는다. 서버 코드 쪽 스위치(GET /config 의 features.costScenario)와 두 겹이다.
 * 기본 꺼짐: 새 사용자 기능이라서
 */
export const costScenarioSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type CostScenarioSettings = z.infer<typeof costScenarioSettingsSchema>
export const DEFAULT_COST_SCENARIO_SETTINGS: CostScenarioSettings = { enabled: false }

/**
 * 배합 버전 이력 (C42, 2026-10-29). 결과 화면 '…' 메뉴의 '버전 기록'(저장·비교·되돌리기).
 * 서버 코드 쪽 스위치(GET /config 의 features.formulaVersions)와 두 겹이다. 둘 다 참일 때만 앱이 메뉴를 보인다.
 * 기본 꺼짐: 새 사용자 기능이라서. 이 스위치는 메뉴(목록·비교·되돌리기)만 연다 — 버전 기록 자체는 견적·샘플 주문이
 * 그때의 배합을 가리키도록 늘 한다 (2026-10-30 Top10 #9, apps/api lib/simulation-versions.ts). 버전은 제품 파기 때 지워진다
 */
export const formulaVersionsSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type FormulaVersionsSettings = z.infer<typeof formulaVersionsSettingsSchema>
export const DEFAULT_FORMULA_VERSIONS_SETTINGS: FormulaVersionsSettings = { enabled: false }

/**
 * 공장 회신 받기 (로드맵 8번, 2026-10-30). 공장 초대 링크 웹 폼(공유 웹 /factory-reply/:token)과 관리자 CSV 양식 가져오기.
 * 서버 코드 쪽 스위치(GET /config 의 features.factoryReplies)와 두 겹이다. 기본 꺼짐: 바깥(공장)에 열리는 새 입구라서.
 * 꺼져 있으면 초대 링크·웹 폼·회신 반영·CSV 반영이 모두 404 다. 받은 회신은 관리자가 반영하기 전에는 공장 목록에 들어가지 않는다
 */
export const factoryRepliesSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type FactoryRepliesSettings = z.infer<typeof factoryRepliesSettingsSchema>
export const DEFAULT_FACTORY_REPLIES_SETTINGS: FactoryRepliesSettings = { enabled: false }
/**
 * 전문가 검수 (로드맵 매출 8위, 2026-10-29). 결과 화면의 '전문가 검수 요청'과 검수권 결제.
 * 서버 코드 쪽 스위치(GET /config 의 features.expertReview)와 두 겹이다. 기본 꺼짐: 스토어 상품(EXPERT_REVIEW_PRODUCT_ID)을
 * 만들기 전이라서. 켜도 앱은 스토어에서 상품 가격을 받아야만 진입점을 그린다. 꺼도 이미 들어온 요청은 관리자 대기열에서
 * 끝까지 처리하고, 처리 기한 반려(검수권 반환)도 그대로 돈다.
 * slaDays: 요청 뒤 이 날수 안에 아무도 맡지 않으면(요청됨 그대로) 서버가 반려하고 검수권을 돌려준다
 */
export const expertReviewSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  slaDays: z.number().int().min(1).max(30).default(7),
})
export type ExpertReviewSettings = z.infer<typeof expertReviewSettingsSchema>
export const DEFAULT_EXPERT_REVIEW_SETTINGS: ExpertReviewSettings = { enabled: false, slaDays: 7 }
/**
 * 생성 10회 팩 (Pro 전용 소모성 상품 generation_pack_10, 로드맵 9위, 2026-10-30). Pro 가 월 생성을 다 써서 결제 화면이 열리면
 * 팩 카드를 보인다. 서버 코드 쪽 스위치(GET /config 의 features.generationPack)와 두 겹이다.
 * 기본 꺼짐: 스토어(App Store Connect·Play Console)에 상품을 만들고 RevenueCat 에 연결한 뒤 켠다 — 상품이 없을 때 켜면
 * 카드가 '가격을 불러오지 못함'으로 보인다. 꺼도 이미 산 팩은 그대로 쓰이고, 환불 회수·결제 반영도 그대로다
 */
export const generationPackSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type GenerationPackSettings = z.infer<typeof generationPackSettingsSchema>
export const DEFAULT_GENERATION_PACK_SETTINGS: GenerationPackSettings = { enabled: false }

/**
 * 생성 횟수 초기화 3일 전 안내 (2026-10-30 대표 결정: 생성 횟수는 이용 기간마다 초기화되고 이월되지 않는다).
 * 매시 크론이 이번 기간이 3일 안에 끝나고 남은 생성이 있는 유료 회원에게 기간마다 한 번 보낸다(거래성 이용 안내).
 * 서버 코드 쪽 스위치(GET /config 의 features.generationResetReminder)와 두 겹이다. 앱 화면은 없다. 기본 꺼짐
 */
export const generationResetReminderSettingsSchema = z.object({
  enabled: z.boolean().default(false),
})
export type GenerationResetReminderSettings = z.infer<typeof generationResetReminderSettingsSchema>
export const DEFAULT_GENERATION_RESET_REMINDER_SETTINGS: GenerationResetReminderSettings = {
  enabled: false,
}
/**
 * 결과 화면 업셀 (로드맵 매출 3위, 2026-10-29). 켜면 결과 맨 아래 '산출물' 칸에 '다음 플랜이면 되는 것' 한 줄과 '플랜 비교'를 늘 보이고,
 * 결과를 나갈 때의 자동 페이월을 뒤로 가기 버튼만이 아니라 밀어서 닫기·안드로이드 뒤로 가기까지 넓히며 '한도 직전' 페이월을 더한다.
 * 자동 페이월 빈도 상한은 result-upsell.ts 의 AUTO_PAYWALL_LIMITS 표 하나가 정한다. 서버 코드 쪽 스위치(GET /config 의 features.resultUpsell)와
 * 두 겹이다. 기본 꺼짐: 새 사용자 기능이라서. 끄면 예전처럼 첫 결과를 뒤로 가기로 나갈 때 한 번만 뜬다
 */
export const resultUpsellSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type ResultUpsellSettings = z.infer<typeof resultUpsellSettingsSchema>
export const DEFAULT_RESULT_UPSELL_SETTINGS: ResultUpsellSettings = { enabled: false }

/**
 * 제품 사양서 (로드맵 매출 5위 '업무 산출물', 2026-10-29). 결과 화면에 제품 정보·배합·공정·원가·표시 사항을 한 장 그림으로
 * 저장하는 버튼을 보인다. 쓸 수 있는 플랜은 planQuotas 의 specSheet(기본 Basic·Pro)이고, 잠긴 플랜이 누르면 페이월이다.
 * 서버 코드 쪽 스위치(GET /config 의 features.specSheet)와 두 겹이다. 기본 꺼짐: 새 사용자 기능이라서
 */
export const specSheetSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type SpecSheetSettings = z.infer<typeof specSheetSettingsSchema>
export const DEFAULT_SPEC_SHEET_SETTINGS: SpecSheetSettings = { enabled: false }
/**
 * 제품 검토 리포트 (C45, 2026-10-30). 결과 화면의 '제품 검토 리포트' 줄 → 다섯 영역 신호등과 고칠 것.
 * 서버 코드 쪽 스위치(GET /config 의 features.productReview)와 두 겹이다. 둘 다 참일 때만 앱이 줄을 보인다.
 * 기본 꺼짐: 새 사용자 기능이라서. 꺼져 있으면 POST /simulations/:id/review 도 404 라 리포트를 새로 저장하지 않는다
 */
export const productReviewSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** 신호등 기준. 기본값은 product-review.ts 의 PRODUCT_REVIEW_THRESHOLDS 한 곳 (대표 결정 2026-10-30) */
  thresholds: productReviewThresholdsSchema.default(PRODUCT_REVIEW_THRESHOLDS),
  /** 제품마다 남기는 리포트 수 */
  keep: z.number().int().min(1).max(100).default(PRODUCT_REVIEW_KEEP),
})
export type ProductReviewSettings = z.infer<typeof productReviewSettingsSchema>
export const DEFAULT_PRODUCT_REVIEW_SETTINGS: ProductReviewSettings = {
  enabled: false,
  thresholds: PRODUCT_REVIEW_THRESHOLDS,
  keep: PRODUCT_REVIEW_KEEP,
}

/**
 * 판매 문구 자동 정리 (C45, 2026-10-30 대표 결정). 생성·AI 수정 결과의 제품명·tagline·먹는 법·소비자 공정에서 인증 단정
 * (HACCP 적합)·확률 단정(출시 성공 확률 80%)·근거 없는 맛 점수(단맛 8)를 효능·질병 표현(P8)처럼 걷어 낸다.
 * 기본 켬: 식품표시광고법 제8조(허위·과장, 인증 사칭 표시)에 걸릴 수 있는 말을 판매 문구에 남기지 않으려고.
 * 끄면 그 세 가지는 걷어 내지 않고 리포트에서 표시만 한다. 효능·질병 표현은 이 스위치와 상관없이 늘 걷어 낸다
 */
export const unsupportedClaimsAutoRemoveSettingsSchema = z.object({
  enabled: z.boolean().default(true),
})
export type UnsupportedClaimsAutoRemoveSettings = z.infer<
  typeof unsupportedClaimsAutoRemoveSettingsSchema
>
export const DEFAULT_UNSUPPORTED_CLAIMS_AUTO_REMOVE_SETTINGS: UnsupportedClaimsAutoRemoveSettings =
  { enabled: true }
/**
 * 결과 화면 비교 블록 (2026-10-30 대표 결정 2). 서버 코드 쪽 스위치(GET /config 의 features.resultBenchmarks)와 두 겹이다.
 * priceCompare: '지금 팔리는 제품'(이미 찾아 둔 경쟁 제품) 값으로 100 g 당 가격 비교. 경쟁 제품처럼 플랜만큼만 보인다.
 * 기본 꺼짐: 새 사용자 기능이라서. 남의 상품 구매 링크는 넣지 않는다
 */
export const resultBenchmarksSettingsSchema = z.object({ priceCompare: z.boolean().default(false) })
export type ResultBenchmarksSettings = z.infer<typeof resultBenchmarksSettingsSchema>
export const DEFAULT_RESULT_BENCHMARKS_SETTINGS: ResultBenchmarksSettings = { priceCompare: false }

/**
 * B2B 광고 (대표 결정 4, 2026-10-30). 원료 대체·포장재·추천 공장·생성 대기·업소용 받는 곳 카드와 신메뉴 식자재 정보.
 * 서버 코드 쪽 스위치(GET /config 의 features.b2bAds)와 두 겹이다. 둘 다 참일 때만 앱이 광고를 받으러 간다.
 * 기본 꺼짐: 새 사용자 기능이고 광고주 계약 전이라서. 광고를 고르는 규칙은 b2b-ads.ts 의 pickAds 하나다.
 * Pro 노출은 대표 미정 — 기본은 결과 화면 광고(도움 정보)는 보이고 생성 대기 광고는 숨긴다
 */
export const b2bAdsSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** 한 화면에 보일 광고 수 (1~2) */
  perScreenMax: z.number().int().min(1).max(2).default(2),
  /** 같은 사람에게 같은 자리 하루 노출 상한 (같은 제품을 다시 열면 세지 않는다) */
  dailyCapPerViewer: z.number().int().min(0).max(50).default(3),
  /** 같은 사람에게 같은 광고 하루 노출 상한 */
  dailyCapPerAd: z.number().int().min(0).max(20).default(1),
  proSeesResultAds: z.boolean().default(true),
  proSeesWaitAds: z.boolean().default(false),
})
export type B2bAdsSettings = z.infer<typeof b2bAdsSettingsSchema>
export const DEFAULT_B2B_ADS_SETTINGS: B2bAdsSettings = {
  enabled: false,
  perScreenMax: 2,
  dailyCapPerViewer: 3,
  dailyCapPerAd: 1,
  proSeesResultAds: true,
  proSeesWaitAds: false,
}
/**
 * AI 중계 샘플·시제품 거래 (2026-10-30, docs/design-ai-brokered-samples.md). 서버 코드 쪽 스위치(GET /config 의
 * features.sampleBroker)와 두 겹이고 기본 꺼짐이다. 결제 공급자는 코드가 정한다(Stripe 키가 없으면 운영은 결제를 시작하지 못한다).
 * - connectionFee: 무료 유형(무료 샘플·정보 제공·무료 시식)도 '결제 뒤 공개' 원칙이라 받는 연결 수수료. 통화별, 비면 그 통화의
 *   무료 유형은 고를 수 없다 (대표 결정 대기)
 * - settlement: 공장 수수료율·정산(지급대행) 방식 자리. 계산에 쓰지 않는다 (공장 계약 대기)
 */
export const sampleBrokerSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  /** AI 중계 메시지 (2부). enabled 와 서버 features.sampleMessages 가 함께 참일 때만 고객·공장 대화가 열린다 */
  messagesEnabled: z.boolean().default(false),
  connectionFee: z
    .object({
      KRW: z.number().positive().max(10_000_000).nullable().default(null),
      USD: z.number().positive().max(10_000).nullable().default(null),
    })
    .prefault({}),
  settlement: z
    .object({
      commissionRatePct: z.number().min(0).max(100).nullable().default(null),
      payout: z.enum(['undecided', 'pg_payout', 'manual']).default('undecided'),
    })
    .prefault({}),
})
export type SampleBrokerSettings = z.infer<typeof sampleBrokerSettingsSchema>
export const DEFAULT_SAMPLE_BROKER_SETTINGS: SampleBrokerSettings =
  sampleBrokerSettingsSchema.parse({})
/**
 * 클릭식 맛 고르기 (2026-10-30 대표 결정 1). 만들기 화면의 맛 축 카드와 결과 화면의 '이 제품의 맛' 카드.
 * 서버 코드 쪽 스위치(GET /config 의 features.tasteAxes)와 두 겹이다. 둘 다 참일 때만 앱이 카드를 그리고,
 * 꺼져 있으면 지금 맛 칩 화면 그대로다. 서버도 꺼져 있으면 생성·수정 요청의 tasteProfile 을 버린다. 기본 꺼짐
 */
export const tasteAxesSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type TasteAxesSettings = z.infer<typeof tasteAxesSettingsSchema>
export const DEFAULT_TASTE_AXES_SETTINGS: TasteAxesSettings = { enabled: false }
/**
 * '더 만들어도 남을까' (대표 Top5, 2026-10-30). 제품 검토 리포트 맨 위 첫 카드(규모별 손익 사다리·품질 유지 조건),
 * 검증 단계 표시(결과 화면·내 제품 목록), 원가 시나리오 서버 계산·버전별 저장, 사양서의 근거 꼬리표·검증 단계.
 * 서버 코드 쪽 스위치(GET /config 의 features.scaleCheck)와 두 겹이다. 기본 꺼짐: 새 사용자 기능이라서.
 * 꺼져 있으면 POST /simulations/:id/cost-scenario·GET /simulations/:id/readiness 는 404, 리포트에 첫 카드가 없다
 */
export const scaleCheckSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type ScaleCheckSettings = z.infer<typeof scaleCheckSettingsSchema>
export const DEFAULT_SCALE_CHECK_SETTINGS: ScaleCheckSettings = { enabled: false }

/**
 * 테스트 결제(샌드박스) 권리 정책 (2026-10-30 대표 Top10 #4, 결정 G 대기). 앱스토어·플레이 샌드박스(TestFlight·앱 심사·라이선스 테스터)와
 * Stripe 테스트 모드 결제가 이 서버에서 권리(구독 플랜·견적권·검수권·생성 팩·샘플 주문 진행)를 받을지 정한다.
 * - all: 모두 받는다 — 지금까지의 동작 (기본값. 대표 결정 전까지 유지)
 * - allowlist: allowedUserIds(심사·QA 계정)만 받는다. Apple 심사관은 샌드박스로 결제하므로 심사 계정을 꼭 넣는다
 * - none: 아무도 받지 않는다
 * 결제 기록(원장)은 정책과 상관없이 남는다. 이미 받은 소모품은 바뀌지 않고, 구독 플랜은 다음 동기화 때 정책대로 맞춘다.
 * 계정 id 가 들어 있어 GET /config 로 내보내지 않는다(서버 전용). 환경(dev·운영)마다 KV 가 따로라 운영에만 바꾸면 된다
 */
export const SANDBOX_RIGHTS_MODES = ['all', 'allowlist', 'none'] as const
export type SandboxRightsMode = (typeof SANDBOX_RIGHTS_MODES)[number]
export const sandboxRightsSettingsSchema = z.object({
  mode: z.enum(SANDBOX_RIGHTS_MODES).default('all'),
  /** Clerk 사용자 id (user_...). 관리자 [사용자]에서 복사한다 */
  allowedUserIds: z.array(z.string().trim().min(1).max(128)).max(50).default([]),
})
export type SandboxRightsSettings = z.infer<typeof sandboxRightsSettingsSchema>
export const DEFAULT_SANDBOX_RIGHTS_SETTINGS: SandboxRightsSettings = {
  mode: 'all',
  allowedUserIds: [],
}

/** 이 사용자의 테스트 결제가 권리를 받는지 */
export function sandboxRightsAllowed(settings: SandboxRightsSettings, userId: string): boolean {
  if (settings.mode === 'all') return true
  return settings.mode === 'allowlist' && settings.allowedUserIds.includes(userId)
}

/**
 * 견적 상태 확장 (명세 P0-02, 2026-10-30 Top10 #5). 관리자 화면의 '공장 확인 중'·'추가 정보 요청' 단계와 앱 견적 상세의 그 안내.
 * 서버 코드 쪽 스위치(GET /config 의 features.quoteStatuses)와 두 겹이다. 꺼져 있으면 관리자는 예전처럼 마감만 하고 앱도 예전 화면이다.
 * 상태 이력과 명세 상태 칸은 스위치와 상관없이 남는다(화면에 보이지 않는 기록). 기본 꺼짐
 */
export const quoteStatusesSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type QuoteStatusesSettings = z.infer<typeof quoteStatusesSettingsSchema>
export const DEFAULT_QUOTE_STATUSES_SETTINGS: QuoteStatusesSettings = { enabled: false }

/**
 * 아이폰 실시간 현황 (2026-10-30 대표 지시, 다이내믹 아일랜드·잠금화면). 서버가 생성 단계·견적 상태·샘플 진행을 APNs 로 보내고,
 * 견적이 도착하면 앱이 꺼져 있어도 원격으로 시작한다. 서버 코드 쪽 스위치(GET /config 의 features.liveActivities)와 두 겹이다.
 * 기본 꺼짐: 1.0.9 빌드(위젯 확장)와 APNs 비밀값(APNS_KEY_P8·APNS_KEY_ID·APNS_TEAM_ID)이 갖춰진 뒤에 켠다.
 * 꺼져 있으면 토큰 경로(/live-activities)가 404 이고 아무 푸시도 보내지 않는다. 시식·조리 타이머는 앱 안에서만 돌아 이 스위치를 보지 않는다
 */
export const liveActivitiesSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type LiveActivitiesSettings = z.infer<typeof liveActivitiesSettingsSchema>
export const DEFAULT_LIVE_ACTIVITIES_SETTINGS: LiveActivitiesSettings = { enabled: false }

/**
 * 홈·잠금화면 위젯과 시리 (2026-10-30 대표 지시). 앱이 GET /widget/summary 를 받아 위젯과 나눠 쓰는 저장소에 둔다.
 * 서버 코드 쪽 스위치(GET /config 의 features.homeWidgets)와 두 겹이다. 기본 꺼짐: 1.0.9 빌드가 나간 뒤에 켠다.
 * 꺼져 있으면 /widget/summary 가 404 이고, 앱은 위젯에 '앱을 열어 주세요'만 남긴다
 */
export const homeWidgetsSettingsSchema = z.object({ enabled: z.boolean().default(false) })
export type HomeWidgetsSettings = z.infer<typeof homeWidgetsSettingsSchema>
export const DEFAULT_HOME_WIDGETS_SETTINGS: HomeWidgetsSettings = { enabled: false }

/**
 * 코스·테이스팅 (대표 확정 명세 2026-11-01 의 0장). 만들기 첫 분류 화면에 셋째 칸 '코스·테이스팅'을 보인다.
 * 서버 코드 쪽 스위치(GET /config 의 features.courseTasting)와 두 겹이다. 둘 다 참일 때만 앱이 칸을 그린다.
 * 기본 켬: 대표가 확정한 명세라 새 앱이 나가면 바로 보이게. 옛 앱은 읽지 않는다. 끄면 칸만 숨고, 이미 만든 코스 제품과
 * 고르던 초안의 생성(axis course)은 서버가 그대로 받는다
 */
export const courseTastingSettingsSchema = z.object({ enabled: z.boolean().default(true) })
export type CourseTastingSettings = z.infer<typeof courseTastingSettingsSchema>
export const DEFAULT_COURSE_TASTING_SETTINGS: CourseTastingSettings = { enabled: true }

/**
 * 인증 준비도 가이드 (2026-11-01, 설계 recipecat-cert-readiness/10_기능_설계.md). 만들기 화면의 '판매·인증 목표' 칩,
 * 생성 프롬프트의 인증 블록, 결과 화면의 준비도 카드. 서버 코드 쪽 스위치(GET /config 의 features.certReadiness)와 두 겹이다.
 * 기본 꺼짐 (대표 결정 2026-11-01): 내부 계정 확인 → 문구·약관 법률 검토 → 전체 순서로 켠다.
 * 끄면 새 생성은 목표를 받지 않고(input 에 남기지 않음), 앱은 칩과 카드를 그리지 않는다. 이미 저장된 준비도는 지우지 않는다.
 * targets 로 목표를 더 좁힐 수 있다 (할랄만 등). 플랜 제한은 없다 — 할랄·해썹과 고칠 것(❌)은 모든 플랜에 연다 (대표 결정).
 */
export const certReadinessSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  targets: z
    .array(z.enum(CERT_TARGETS))
    .max(4)
    .default([...CERT_TARGETS_AVAILABLE]),
})
export type CertReadinessSettings = z.infer<typeof certReadinessSettingsSchema>
export const DEFAULT_CERT_READINESS_SETTINGS: CertReadinessSettings = {
  enabled: false,
  targets: [...CERT_TARGETS_AVAILABLE],
}

/**
 * 어드민에서 조정하는 앱 설정. KV(FEATURE_FLAGS) 의 settings:v1 에 저장.
 * 앱은 GET /config 로 읽고, 어드민은 GET/PUT /admin/settings 로 편집한다.
 */
export const appSettingsSchema = z.object({
  /** Durable PC factory sync, off until deployment and operator acceptance. */
  manufacturing: z.object({ enabled: z.boolean().default(false) }).default({ enabled: false }),
  /**
   * 로그인 없이 기기당 돌릴 수 있는 시뮬레이션 횟수 (서버가 device id 로 센다, 실패 건 제외). 0 = 무제한.
   * 기본값은 2 (2026-10-09, 대표 결정. 무료 회원 생성 수와 같게). 운영 KV settings:v1 에 값이 있으면 그 값이 우선한다
   */
  guestSimulationLimit: z.number().int().min(0).max(1000),
  /** 손님 생성 남용 한도 (요청 IP 하루, 전체 1시간). 예전 KV 값에 없으면 기본값(켬)이 채워진다 */
  guestAbuseLimits: guestAbuseLimitsSettingsSchema.default(DEFAULT_GUEST_ABUSE_LIMITS_SETTINGS),
  /** 플랜별 한도 (월 생성 · 사진 · 턴 …) */
  planQuotas: planQuotasSchema.default(DEFAULT_PLAN_QUOTAS),
  /**
   * 푸시 테스트용: 비어 있지 않으면 비회원 리마인더를 '삭제 N시간 전' 대신 결과가 나온 뒤 이 분(分) 간격으로 보낸다
   * (예: [2, 4] → 2분 뒤·4분 뒤). production 환경에서는 무시된다. 테스트가 끝나면 비운다
   */
  guestReminderTestMinutes: z.array(z.number().min(1).max(180)).max(8).default([]),
  /** 새 버전 안내 (최소·권장 버전, 스토어 주소). 예전 KV 값에 없으면 기본값(안내 없음)이 채워진다 */
  appUpdate: appUpdateSettingsSchema.default(DEFAULT_APP_UPDATE_SETTINGS),
  /**
   * Apple·Google 간편 로그인 버튼 노출 (iOS·Android 따로). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다.
   * 관리자 [설정] › 간편 로그인에서 편집하고, 1.0.1 부터 앱이 읽는다
   */
  socialSignIn: socialSignInSettingsSchema.default(DEFAULT_SOCIAL_SIGN_IN_SETTINGS),
  /** 완성 사례 갤러리 (갤러리 보이기, 시작하기 허용). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  sampleCases: sampleCasesSettingsSchema.default(DEFAULT_SAMPLE_CASES_SETTINGS),
  /** 홈 위 공지 띠 (C20). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  appNotice: appNoticeSettingsSchema.default(DEFAULT_APP_NOTICE_SETTINGS),
  /** 결과 화면 AI 맛 예측 카드. 예전 KV 값에 없으면 기본값(켬)이 채워진다 */
  tastePrediction: tastePredictionSettingsSchema.default(DEFAULT_TASTE_PREDICTION_SETTINGS),
  /** 견적 접수 (C36). 예전 KV 값에 없으면 기본값(켬)이 채워진다 */
  quoteIntake: quoteIntakeSettingsSchema.default(DEFAULT_QUOTE_INTAKE_SETTINGS),
  /** 아이디어 시작 질문 (C39). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  startQuestions: startQuestionsSettingsSchema.default(DEFAULT_START_QUESTIONS_SETTINGS),
  /** '어떻게 알고 오셨어요?' (C56). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  heardFromSurvey: heardFromSurveySettingsSchema.default(DEFAULT_HEARD_FROM_SURVEY_SETTINGS),
  /** 웹 공유 공개 범위 선택 (C54). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  shareScope: shareScopeSettingsSchema.default(DEFAULT_SHARE_SCOPE_SETTINGS),
  /** 생성 취소 (C50). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  generationCancel: generationCancelSettingsSchema.default(DEFAULT_GENERATION_CANCEL_SETTINGS),
  /** 실패한 사진만 다시 만들기 (C49). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  photoRetryFailed: failedPhotoRetrySettingsSchema.default(DEFAULT_FAILED_PHOTO_RETRY_SETTINGS),
  /** 견적 취소 버튼·0건 마감 반환 (C48). 예전 KV 값에 없으면 기본값(버튼 꺼짐, 0건 반환 켬)이 채워진다 */
  quoteCancel: quoteCancelSettingsSchema.default(DEFAULT_QUOTE_CANCEL_SETTINGS),
  /** 스토어 환불 회수 (C51). 예전 KV 값에 없으면 기본값(켬)이 채워진다 */
  refundRevoke: refundRevokeSettingsSchema.default(DEFAULT_REFUND_REVOKE_SETTINGS),
  /** 광고성 알림 수신 동의 선택 화면 (로드맵 P6). 예전 KV 값에 없으면 기본값(켬)이 채워진다 */
  marketingConsent: marketingConsentSettingsSchema.default(DEFAULT_MARKETING_CONSENT_SETTINGS),
  /** 견적 가능 시장 (로드맵 P7). 예전 KV 값에 없으면 기본값(켬, 모든 시장)이 채워진다 */
  quoteMarkets: quoteMarketsSettingsSchema.default(DEFAULT_QUOTE_MARKETS_SETTINGS),
  /** 재방문 알림 (한도 리마인더·견적 유도). 예전 KV 값에 없으면 기본값(둘 다 꺼짐)이 채워진다 */
  revisitNotifications: revisitNotificationsSettingsSchema.default(
    DEFAULT_REVISIT_NOTIFICATIONS_SETTINGS,
  ),
  /** 결과 화면 원가 시나리오 (C46). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  costScenario: costScenarioSettingsSchema.default(DEFAULT_COST_SCENARIO_SETTINGS),
  /** 배합 버전 이력 (C42). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  formulaVersions: formulaVersionsSettingsSchema.default(DEFAULT_FORMULA_VERSIONS_SETTINGS),
  /** 배정 운영자 접근 (C52). 관리자 API 전용 — 앱은 읽지 않는다. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  accessGrants: accessGrantsSettingsSchema.default(DEFAULT_ACCESS_GRANTS_SETTINGS),
  /** AI 기능 필수 동의 (2026-10-29). 예전 KV 값에 없으면 기본값(둘 다 꺼짐)이 채워진다 */
  aiConsent: aiConsentSettingsSchema.default(DEFAULT_AI_CONSENT_SETTINGS),
  /** 공장 회신 받기 (로드맵 8번). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  factoryReplies: factoryRepliesSettingsSchema.default(DEFAULT_FACTORY_REPLIES_SETTINGS),
  /** 전문가 검수 (로드맵 매출 8위). 예전 KV 값에 없으면 기본값(꺼짐, 기한 7일)이 채워진다 */
  expertReview: expertReviewSettingsSchema.default(DEFAULT_EXPERT_REVIEW_SETTINGS),
  /** 생성 10회 팩 카드 (로드맵 9위). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  generationPack: generationPackSettingsSchema.default(DEFAULT_GENERATION_PACK_SETTINGS),
  /** 생성 횟수 초기화 3일 전 안내. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  generationResetReminder: generationResetReminderSettingsSchema.default(
    DEFAULT_GENERATION_RESET_REMINDER_SETTINGS,
  ),
  /** 결과 화면 업셀·자동 페이월 넓히기. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  resultUpsell: resultUpsellSettingsSchema.default(DEFAULT_RESULT_UPSELL_SETTINGS),
  /** 제품 사양서 저장 버튼. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  specSheet: specSheetSettingsSchema.default(DEFAULT_SPEC_SHEET_SETTINGS),
  /** 제품 검토 리포트 (C45). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  productReview: productReviewSettingsSchema.default(DEFAULT_PRODUCT_REVIEW_SETTINGS),
  /** 판매 문구 자동 정리 (C45). 예전 KV 값에 없으면 기본값(켬)이 채워진다 */
  unsupportedClaimsAutoRemove: unsupportedClaimsAutoRemoveSettingsSchema.default(
    DEFAULT_UNSUPPORTED_CLAIMS_AUTO_REMOVE_SETTINGS,
  ),
  /** 결과 화면 비교 블록 (시판 제품 100 g 당 가격). 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  resultBenchmarks: resultBenchmarksSettingsSchema.default(DEFAULT_RESULT_BENCHMARKS_SETTINGS),
  /** B2B 광고 (대표 결정 4, 기본 꺼짐) */
  b2bAds: b2bAdsSettingsSchema.default(DEFAULT_B2B_ADS_SETTINGS),
  /** AI 중계 샘플 거래. 예전 KV 값에 없으면 기본값(꺼짐, 연결 수수료 없음)이 채워진다 */
  sampleBroker: sampleBrokerSettingsSchema.default(DEFAULT_SAMPLE_BROKER_SETTINGS),
  /** 클릭식 맛 고르기. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  tasteAxes: tasteAxesSettingsSchema.default(DEFAULT_TASTE_AXES_SETTINGS),
  /** '더 만들어도 남을까' 첫 카드·검증 단계. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  scaleCheck: scaleCheckSettingsSchema.default(DEFAULT_SCALE_CHECK_SETTINGS),
  /** 테스트 결제 권리 정책 (서버 전용). 예전 KV 값에 없으면 기본값(모두 받음 = 지금 동작)이 채워진다 */
  sandboxRights: sandboxRightsSettingsSchema.default(DEFAULT_SANDBOX_RIGHTS_SETTINGS),
  /** 견적 상태 확장. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  quoteStatuses: quoteStatusesSettingsSchema.default(DEFAULT_QUOTE_STATUSES_SETTINGS),
  /** 아이폰 실시간 현황. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  liveActivities: liveActivitiesSettingsSchema.default(DEFAULT_LIVE_ACTIVITIES_SETTINGS),
  /** 홈·잠금화면 위젯과 시리. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  homeWidgets: homeWidgetsSettingsSchema.default(DEFAULT_HOME_WIDGETS_SETTINGS),
  /** 코스·테이스팅 칸. 예전 KV 값에 없으면 기본값(켬)이 채워진다 */
  courseTasting: courseTastingSettingsSchema.default(DEFAULT_COURSE_TASTING_SETTINGS),
  /** 인증 준비도 가이드. 예전 KV 값에 없으면 기본값(꺼짐)이 채워진다 */
  certReadiness: certReadinessSettingsSchema.default(DEFAULT_CERT_READINESS_SETTINGS),
})
export type AppSettings = z.infer<typeof appSettingsSchema>

/** GET /config 응답. 필수 설정이 빠지거나 잘못되면 앱은 세션 기본값을 쓴다. */
export const appConfigResponseSchema = z.object({
  settings: appSettingsSchema,
  shareUrl: z.string().optional(),
  fx: z
    .object({
      USD: z.number().positive(),
      KRW: z.number().positive(),
      JPY: z.number().positive(),
      CNY: z.number().positive(),
    })
    .partial()
    .optional(),
  features: z
    .object({
      simRetry: z.unknown().optional(),
      manufacturing: z.unknown().optional(),
      /**
       * GET /cases 가 있는 서버 (2026-10-22). 없으면 앱은 완성 사례 진입점을 그리지 않는다.
       * simRetry 처럼 형을 묻지 않는다. 형이 틀려도 설정 전체가 기본값으로 돌아가지 않게 하고, 앱은 === true 만 켠다
       */
      sampleCases: z.unknown().optional(),
      /** POST·GET /tastings 가 있는 서버 (2026-10-25). 없으면 앱은 견적 화면의 시식 기록 진입점을 그리지 않는다 */
      tastings: z.unknown().optional(),
      /**
       * GET /tastings/prediction 이 레시피로 맛을 추정하는 서버 (2026-10-25). 없으면 앱은 AI 맛 예측 카드를 그리지 않는다.
       * 관리자 settings.tastePrediction.enabled 와 둘 다 참일 때만 그린다
       */
      tastePrediction: z.unknown().optional(),
      /**
       * POST /quotes 가 관리자 settings.quoteIntake 를 따르는 서버 (C36). 끈 동안 quote-intake 를 실은 새 앱의 접수를 거절한다
       */
      quoteIntake: z.unknown().optional(),
      /** POST /simulations 가 startGoals 를 받아 프롬프트에 쓰는 서버 (C39). settings.startQuestions.enabled 와 둘 다 참일 때만 질문을 그린다 */
      startQuestions: z.unknown().optional(),
      /** POST /simulations 가 productTags(식품 축 속성 꼬리표, 2026-11-01)를 받아 프롬프트에 쓰는 서버. 앱은 이때만 고르기 카드를 그린다 */
      productTags: z.unknown().optional(),
      /** POST /quotes 가 request.heardFrom 을 받아 남기는 서버 (C56). settings.heardFromSurvey.enabled 와 둘 다 참일 때만 질문을 그린다 */
      heardFrom: z.unknown().optional(),
      /** POST /simulations/:id/share 가 scope 를 받는 서버 (C54). settings.shareScope.enabled 와 둘 다 참일 때만 선택을 그린다 */
      shareScope: z.unknown().optional(),
      /** POST /simulations/:id/cancel 이 있는 서버 (C50). settings.generationCancel.enabled 와 둘 다 참일 때만 취소 버튼을 그린다 */
      generationCancel: z.unknown().optional(),
      /** POST /simulations/:id/photos/retry-failed 가 있는 서버 (C49). settings.photoRetryFailed.enabled 와 둘 다 참일 때만 버튼을 그린다 */
      photoRetryFailed: z.unknown().optional(),
      /** POST /quotes/:id/cancel 이 있는 서버 (C48). settings.quoteCancel.enabled 와 둘 다 참일 때만 취소 버튼을 그린다 */
      quoteCancel: z.unknown().optional(),
      /**
       * GET·PUT /me/marketing-consent 가 있고 광고성 푸시를 동의한 사람에게만 보내는 서버 (로드맵 P6).
       * 없으면 앱은 선택 화면과 계정 탭 토글을 그리지 않는다. 선택 화면은 settings.marketingConsent.promptEnabled 와 둘 다 참일 때만
       */
      marketingConsent: z.unknown().optional(),
      /**
       * POST /quotes 가 settings.quoteMarkets 밖의 시장 제품을 거절하고 POST /quotes/waitlist 가 있는 서버 (로드맵 P7).
       * settings.quoteMarkets.enabled 와 둘 다 참일 때만 앱이 시장 밖 제품의 견적 버튼에서 대기 명단을 안내한다
       */
      quoteMarkets: z.unknown().optional(),
      /** 원가 시나리오 설정(settings.costScenario)을 아는 서버 (C46). settings.costScenario.enabled 와 둘 다 참일 때만 결과 화면에 칸을 그린다 */
      costScenario: z.unknown().optional(),
      /** GET /simulations/:id/versions 가 있고 저장 때 버전을 남기는 서버 (C42). settings.formulaVersions.enabled 와 둘 다 참일 때만 메뉴를 그린다 */
      formulaVersions: z.unknown().optional(),
      /**
       * GET·PUT /me/ai-consent 가 있고 AI 요청을 settings.aiConsent 에 따라 거절하는 서버 (2026-10-29 AI 필수 동의).
       * settings.aiConsent.required 와 둘 다 참일 때만 앱이 서버 기록으로 필수 동의를 받는다
       */
      aiConsent: z.unknown().optional(),
      /** 공장 회신 받기 경로가 있는 서버 (로드맵 8번). 관리자 화면이 settings.factoryReplies.enabled 와 둘 다 참일 때만 보인다 */
      factoryReplies: z.unknown().optional(),
      /** GET·POST /expert-reviews 가 있는 서버 (로드맵 매출 8위). settings.expertReview.enabled 와 둘 다 참이고 스토어 상품이 있을 때만 진입점을 그린다 */
      expertReview: z.unknown().optional(),
      /**
       * Pro 생성 팩(generation_pack_10)의 구매 반영·소진·환불 회수를 갖춘 서버 (로드맵 9위). settings.generationPack.enabled 와
       * 둘 다 참일 때만 앱이 결제 화면에 팩 카드를 그린다
       */
      generationPack: z.unknown().optional(),
      /** 결과 화면 업셀을 아는 서버 (2026-10-29). settings.resultUpsell.enabled 와 둘 다 참일 때만 업셀 줄·넓힌 자동 페이월 */
      resultUpsell: z.unknown().optional(),
      /** GET /me/plan 의 quota.specSheet 가 있는 서버 (2026-10-29). settings.specSheet.enabled 와 둘 다 참일 때만 사양서 버튼 */
      specSheet: z.unknown().optional(),
      /** POST /simulations/:id/review 가 있는 서버 (C45). settings.productReview.enabled 와 둘 다 참일 때만 결과 화면에 줄을 그린다 */
      productReview: z.unknown().optional(),
      /** settings.resultBenchmarks 를 내보내는 서버 (2026-10-30). 둘 다 참일 때만 결과 화면에 가격 비교를 그린다 */
      resultBenchmarks: z.unknown().optional(),
      /** POST /ads/pick·클릭·문의가 있는 서버 (B2B 광고). settings.b2bAds.enabled 와 둘 다 참일 때만 앱이 광고를 받으러 간다 */
      b2bAds: z.unknown().optional(),
      /** /sample-orders·/factory-samples 가 있는 서버 (AI 중계 샘플). settings.sampleBroker.enabled 와 둘 다 참일 때만 샘플 진입점을 그린다 */
      sampleBroker: z.unknown().optional(),
      /** /sample-orders/:id/messages 가 있는 서버 (AI 중계 메시지). settings.sampleBroker.messagesEnabled 와 둘 다 참일 때만 대화를 그린다 */
      sampleMessages: z.unknown().optional(),
      /** POST /simulations·/edit 가 tasteProfile 을 받아 플랜으로 거르고 결과에 맛 값을 남기는 서버. settings.tasteAxes.enabled 와 둘 다 참일 때만 맛 축 카드를 그린다 */
      tasteAxes: z.unknown().optional(),
      /** POST /simulations/:id/cost-scenario·GET /simulations/:id/readiness 가 있는 서버 (Top5). settings.scaleCheck.enabled 와 둘 다 참일 때만 첫 카드·검증 단계를 그린다 */
      scaleCheck: z.unknown().optional(),
      /** 명세 견적 상태(requestStatus·offerStatus)와 상태 이력을 내보내는 서버. settings.quoteStatuses.enabled 와 둘 다 참일 때만 새 단계를 그린다 */
      quoteStatuses: z.unknown().optional(),
      /** POST /live-activities 가 있고 생성·견적·샘플 진행을 APNs 로 보내는 서버. settings.liveActivities.enabled 와 둘 다 참일 때만 앱이 토큰을 알린다 */
      liveActivities: z.unknown().optional(),
      /** GET /widget/summary 가 있는 서버. settings.homeWidgets.enabled 와 둘 다 참일 때만 앱이 위젯 요약을 받아 둔다 */
      homeWidgets: z.unknown().optional(),
      /** POST /simulations 가 axis course(코스·테이스팅)를 받는 서버. settings.courseTasting.enabled 와 둘 다 참일 때만 앱이 칸을 그린다 */
      courseTasting: z.unknown().optional(),
      /** 생성 요청의 certGoals 를 받아 프롬프트·심사·준비도(result.certReadiness)에 쓰는 서버. settings.certReadiness.enabled 와 둘 다 참일 때만 앱이 칩과 카드를 그린다 */
      certReadiness: z.unknown().optional(),
    })
    .optional(),
})

/**
 * 게스트 생성 기본 한도 (2026-10-09, 이전 0 = 무제한). 정상 앱 사용자의 기기당 한도(UX)일 뿐이다.
 * 서버는 클라이언트가 보내는 X-Device-Id 로 세므로, 기기 id 를 바꾸거나(요청마다 새 UUID, 앱 재설치)
 * 같은 id 로 동시에 보내는 것은 막지 못한다. 그 비용 방어는 guestAbuseLimits(요청 IP 하루 · 전체 1시간)가 맡는다.
 */
export const DEFAULT_GUEST_SIMULATION_LIMIT = 2

export const DEFAULT_APP_SETTINGS: AppSettings = {
  manufacturing: { enabled: false },
  guestSimulationLimit: DEFAULT_GUEST_SIMULATION_LIMIT,
  guestAbuseLimits: DEFAULT_GUEST_ABUSE_LIMITS_SETTINGS,
  planQuotas: DEFAULT_PLAN_QUOTAS,
  guestReminderTestMinutes: [],
  appUpdate: DEFAULT_APP_UPDATE_SETTINGS,
  socialSignIn: DEFAULT_SOCIAL_SIGN_IN_SETTINGS,
  sampleCases: DEFAULT_SAMPLE_CASES_SETTINGS,
  appNotice: DEFAULT_APP_NOTICE_SETTINGS,
  tastePrediction: DEFAULT_TASTE_PREDICTION_SETTINGS,
  quoteIntake: DEFAULT_QUOTE_INTAKE_SETTINGS,
  startQuestions: DEFAULT_START_QUESTIONS_SETTINGS,
  heardFromSurvey: DEFAULT_HEARD_FROM_SURVEY_SETTINGS,
  shareScope: DEFAULT_SHARE_SCOPE_SETTINGS,
  generationCancel: DEFAULT_GENERATION_CANCEL_SETTINGS,
  photoRetryFailed: DEFAULT_FAILED_PHOTO_RETRY_SETTINGS,
  quoteCancel: DEFAULT_QUOTE_CANCEL_SETTINGS,
  refundRevoke: DEFAULT_REFUND_REVOKE_SETTINGS,
  marketingConsent: DEFAULT_MARKETING_CONSENT_SETTINGS,
  quoteMarkets: DEFAULT_QUOTE_MARKETS_SETTINGS,
  revisitNotifications: DEFAULT_REVISIT_NOTIFICATIONS_SETTINGS,
  costScenario: DEFAULT_COST_SCENARIO_SETTINGS,
  formulaVersions: DEFAULT_FORMULA_VERSIONS_SETTINGS,
  accessGrants: DEFAULT_ACCESS_GRANTS_SETTINGS,
  aiConsent: DEFAULT_AI_CONSENT_SETTINGS,
  factoryReplies: DEFAULT_FACTORY_REPLIES_SETTINGS,
  expertReview: DEFAULT_EXPERT_REVIEW_SETTINGS,
  generationPack: DEFAULT_GENERATION_PACK_SETTINGS,
  generationResetReminder: DEFAULT_GENERATION_RESET_REMINDER_SETTINGS,
  resultUpsell: DEFAULT_RESULT_UPSELL_SETTINGS,
  specSheet: DEFAULT_SPEC_SHEET_SETTINGS,
  productReview: DEFAULT_PRODUCT_REVIEW_SETTINGS,
  unsupportedClaimsAutoRemove: DEFAULT_UNSUPPORTED_CLAIMS_AUTO_REMOVE_SETTINGS,
  resultBenchmarks: DEFAULT_RESULT_BENCHMARKS_SETTINGS,
  b2bAds: DEFAULT_B2B_ADS_SETTINGS,
  sampleBroker: DEFAULT_SAMPLE_BROKER_SETTINGS,
  tasteAxes: DEFAULT_TASTE_AXES_SETTINGS,
  scaleCheck: DEFAULT_SCALE_CHECK_SETTINGS,
  sandboxRights: DEFAULT_SANDBOX_RIGHTS_SETTINGS,
  quoteStatuses: DEFAULT_QUOTE_STATUSES_SETTINGS,
  liveActivities: DEFAULT_LIVE_ACTIVITIES_SETTINGS,
  homeWidgets: DEFAULT_HOME_WIDGETS_SETTINGS,
  courseTasting: DEFAULT_COURSE_TASTING_SETTINGS,
  certReadiness: DEFAULT_CERT_READINESS_SETTINGS,
}

export const APP_SETTINGS_KV_KEY = 'settings:v1'
